// Is this tab/app running an older build than the one that's live on its site?
//
// Open tabs and apps kept in memory (iPhones especially) keep running the build they loaded - after a
// release, sometimes for days. Every page carries its build id (__NEXT_DATA__.buildId), and the HTML
// is served no-cache, so fetching "/" from the same site tells us what's live right now: no backend
// or version database needed, and each Static Web App answers for its own deployment.

export const VERSION_RELOAD_KEY = 'versionCheck.lastReload';
export const VERSION_RELOAD_MIN_INTERVAL_MS = 5 * 60 * 1000;

export function runningBuildId(): string | null {
  if (typeof window === 'undefined') return null;
  return (window as any).__NEXT_DATA__?.buildId ?? null;
}

export function extractBuildId(html: string): string | null {
  const match = /"buildId"\s*:\s*"([^"]+)"/.exec(html);
  return match ? match[1] : null;
}

// The build id currently live on this site, or null if it couldn't be determined (offline, error
// page...) - in which case nothing should happen.
export async function fetchLiveBuildId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const response = await fetchImpl('/?versionCheck=' + Date.now(), { cache: 'no-store' });
    if (!response.ok) return null;
    return extractBuildId(await response.text());
  } catch {
    return null;
  }
}

// True only when both ids are known and differ. The dev server has no real build id to compare.
export async function isOutdated(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const running = runningBuildId();
  if (!running || running === 'development') return false;
  const live = await fetchLiveBuildId(fetchImpl);
  return !!live && live !== running;
}

// Reloads at most once per interval per tab (sessionStorage), so a site that briefly serves mixed
// versions mid-deploy can't trap anyone in a reload loop. Returns whether it reloaded.
export function reloadForNewVersion(reload: () => void = () => window.location.reload()): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(VERSION_RELOAD_KEY)) || 0;
  } catch {}
  if (Date.now() - last < VERSION_RELOAD_MIN_INTERVAL_MS) return false;

  try {
    sessionStorage.setItem(VERSION_RELOAD_KEY, String(Date.now()));
  } catch {}
  reload();
  return true;
}
